import { 
  prepareElementsForExport, 
  restoreElementsAfterExport, 
  getFullContentDimensions,
  prepareClonedDocumentForExport,
  injectExportStyles,
  removeExportStyles
} from './exportHelpers';

/**
 * Exporta o canvas como imagem PNG
 */
export const exportCanvasToImage = async (
  canvasElement: HTMLElement,
  projectName: string = 'Workflow',
  format: 'png' | 'jpg' = 'png'
): Promise<void> => {
  let savedStyles: ReturnType<typeof prepareElementsForExport> = [];
  let exportStyleElement: HTMLStyleElement | null = null;
  
  try {
    console.log('[exportImage] Iniciando exportação de imagem...');
    console.log('[exportImage] Elemento recebido:', canvasElement);
    console.log('[exportImage] Elemento id:', canvasElement.id);
    console.log('[exportImage] Elemento dimensões originais:', { 
      offsetWidth: canvasElement.offsetWidth, 
      offsetHeight: canvasElement.offsetHeight,
      scrollWidth: canvasElement.scrollWidth,
      scrollHeight: canvasElement.scrollHeight
    });
    
    // Importar html2canvas dinamicamente
    const html2canvas = (await import('html2canvas')).default;
    
    console.log('[exportImage] html2canvas importado com sucesso');
    
    // Injetar estilos CSS temporários para exportação (apenas no clone, não no original)
    exportStyleElement = injectExportStyles();
    
    // Obter dimensões completas do conteúdo (sem modificar elementos visíveis)
    const { width, height } = getFullContentDimensions(canvasElement);
    
    console.log('[exportImage] Dimensões calculadas:', { width, height });
    
    if (width <= 0 || height <= 0) {
      throw new Error(`Dimensões inválidas: width=${width}, height=${height}`);
    }
    
    // Criar canvas da imagem com dimensões completas
    const canvas = await html2canvas(canvasElement, {
      backgroundColor: '#ffffff',
      scale: 2, // Qualidade balanceada para imagem
      useCORS: true,
      logging: false,
      allowTaint: true,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
      width: width,
      height: height,
      windowWidth: width,
      windowHeight: height,
      onclone: (clonedDoc) => {
        // Preparar documento clonado para exportação (aqui modificamos apenas o clone)
        prepareClonedDocumentForExport(clonedDoc, 'canvas-content');
        
        // Preparar elementos no clone (não no original)
        const clonedCanvasContent = clonedDoc.querySelector('#canvas-content') as HTMLElement;
        if (clonedCanvasContent) {
          // Preparar elementos do clone para exportação
          savedStyles = prepareElementsForExport(clonedCanvasContent);
        }
        
        // Ajustar dimensões do container clonado
        const clonedElement = clonedDoc.querySelector('#canvas-content') as HTMLElement;
        if (clonedElement) {
          clonedElement.style.overflow = 'visible';
          clonedElement.style.position = 'relative';
          clonedElement.style.width = `${width}px`;
          clonedElement.style.height = `${height}px`;
          clonedElement.style.transform = 'none';
          clonedElement.style.left = '0px';
          clonedElement.style.top = '0px';
          
          // Garantir que SVGs sejam visíveis e capturados
          const svgs = clonedElement.querySelectorAll('svg');
          svgs.forEach((svg) => {
            svg.style.visibility = 'visible';
            svg.style.display = 'block';
            svg.style.overflow = 'visible';
            svg.style.position = 'absolute';
            svg.style.left = '0';
            svg.style.top = '0';
            // Garantir que o SVG tenha dimensões explícitas
            svg.setAttribute('width', String(width));
            svg.setAttribute('height', String(height));
            svg.style.width = `${width}px`;
            svg.style.height = `${height}px`;
          });
          
          // Garantir que todos os paths SVG sejam visíveis
          const paths = clonedElement.querySelectorAll('svg path');
          paths.forEach((path) => {
            const pathEl = path as SVGPathElement;
            pathEl.style.visibility = 'visible';
            pathEl.style.display = 'block';
            
            // Se o path tem stroke="transparent", é um path de área clicável - ocultar
            const stroke = pathEl.getAttribute('stroke');
            if (stroke === 'transparent') {
              pathEl.style.display = 'none';
            }
          });
          
          // Garantir que grupos SVG sejam visíveis
          const gElements = clonedElement.querySelectorAll('svg g');
          gElements.forEach((g) => {
            (g as SVGGElement).style.visibility = 'visible';
            (g as SVGGElement).style.display = 'block';
          });
          
          // Garantir que markers SVG sejam visíveis
          const markers = clonedElement.querySelectorAll('svg marker');
          markers.forEach((marker) => {
            (marker as SVGMarkerElement).style.visibility = 'visible';
          });
          
          // Garantir que polygons (setas) sejam visíveis
          const polygons = clonedElement.querySelectorAll('svg polygon');
          polygons.forEach((polygon) => {
            (polygon as SVGPolygonElement).style.visibility = 'visible';
          });
          
          // Garantir que todos os cards sejam visíveis e não cortados
          const cards = clonedElement.querySelectorAll('[class*="absolute"]');
          cards.forEach((card) => {
            const cardEl = card as HTMLElement;
            const computedStyle = clonedDoc.defaultView?.getComputedStyle(cardEl);
            
            // Verificar se é card postit - manter altura fixa
            const isPostItCard = cardEl.classList.toString().includes('postit') || 
                                cardEl.closest('[class*="postit"]') !== null;
            
            // Verificar se é card de decisão (losango) - NÃO modificar
            const isDecisionCard = cardEl.getAttribute('data-card-type') === 'decisao' ||
                                  cardEl.closest('[data-card-type="decisao"]') !== null;
            
            // Verificar se é conteúdo de decisão (texto, labels) - NÃO modificar
            const isDecisionContent = cardEl.getAttribute('data-decision-content') === 'true' ||
                                      cardEl.closest('[data-decision-content="true"]') !== null;
            
            // Verificar se elemento tem translate (centralização) - preservar
            const hasTranslate = cardEl.classList.toString().includes('translate');
            
            // Remover transições e animações
            cardEl.style.transition = 'none';
            cardEl.style.animation = 'none';
            cardEl.style.willChange = 'auto';
            
            // Pular cards de decisão, conteúdo de decisão e elementos com translate - não modificar seus estilos
            if (isDecisionCard || isDecisionContent || hasTranslate) {
              return;
            }
            
            if (!isPostItCard) {
              cardEl.style.overflow = 'visible';
              cardEl.style.overflowY = 'visible';
              cardEl.style.overflowX = 'visible';
              cardEl.style.maxHeight = 'none';
              
              // Garantir que altura seja auto para mostrar todo o conteúdo (exceto postit)
              if (computedStyle && computedStyle.height !== 'auto') {
                const scrollHeight = cardEl.scrollHeight || parseFloat(computedStyle.height) || 0;
                if (scrollHeight > parseFloat(computedStyle.height)) {
                  cardEl.style.height = 'auto';
                  cardEl.style.minHeight = computedStyle.height;
                }
              }
            } else {
              // Para postit, manter altura fixa
              cardEl.style.height = '160px';
              cardEl.style.minHeight = '160px';
              cardEl.style.maxHeight = '160px';
            }
            
            // Garantir que losangos mantenham transform
            if (cardEl.querySelector('[class*="rotate-45"]')) {
              const diamond = cardEl.querySelector('[class*="rotate-45"]') as HTMLElement;
              if (diamond) {
                diamond.style.transform = 'rotate(45deg)';
                diamond.style.width = '160px';
                diamond.style.height = '160px';
                diamond.style.transition = 'none';
                diamond.style.overflow = 'visible';
                
                // Garantir que o conteúdo interno do losango também tenha a contra-rotação
                const innerContent = diamond.querySelector('[class*="-rotate-45"]') as HTMLElement;
                if (innerContent) {
                  innerContent.style.transform = 'rotate(-45deg)';
                }
                
                // Procurar todos os filhos diretos e aplicar contra-rotação se necessário
                Array.from(diamond.children).forEach((child) => {
                  const childEl = child as HTMLElement;
                  if (childEl.classList && childEl.classList.toString().includes('-rotate-45')) {
                    childEl.style.transform = 'rotate(-45deg)';
                  }
                });
              }
            }
            
            // Se o próprio elemento é um losango
            if (cardEl.classList.toString().includes('rotate-45') && !cardEl.classList.toString().includes('-rotate-45')) {
              cardEl.style.transform = 'rotate(45deg)';
              cardEl.style.width = '160px';
              cardEl.style.height = '160px';
            }
            
            // Se é conteúdo interno de losango (contra-rotação)
            if (cardEl.classList.toString().includes('-rotate-45')) {
              cardEl.style.transform = 'rotate(-45deg)';
            }
          });
        }
      },
    });

    // Não precisamos restaurar estilos do elemento original pois não os modificamos

    // Converter para blob e fazer download
    const mimeType = format === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = format === 'jpg' ? 0.95 : undefined;
    
    canvas.toBlob((blob) => {
      if (!blob) {
        throw new Error('Falha ao criar o arquivo de imagem.');
      }
      
      // Criar URL temporária
      const url = URL.createObjectURL(blob);
      
      // Criar link de download
      const link = document.createElement('a');
      link.href = url;
      
      // Sanitizar nome do arquivo
      const sanitizedName = projectName.replace(/[^a-z0-9]/gi, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      const extension = format === 'jpg' ? 'jpg' : 'png';
      link.download = `${sanitizedName}_${dateStr}.${extension}`;
      
      // Disparar download
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Restaurar estilos dos elementos do clone (se houver)
      if (savedStyles && savedStyles.length > 0) {
        restoreElementsAfterExport(savedStyles);
      }
      
      // Remover estilos CSS temporários
      removeExportStyles(exportStyleElement);
    }, mimeType, quality);
  } catch (error) {
    console.error('Erro ao exportar imagem:', error);
    // Restaurar estilos mesmo em caso de erro (apenas se foram modificados)
    if (savedStyles && savedStyles.length > 0) {
      restoreElementsAfterExport(savedStyles);
    }
    removeExportStyles(exportStyleElement);
    throw new Error('Falha ao exportar imagem. Tente novamente.');
  }
};

